import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Share2,
  Upload,
  Download,
  FileText,
  File,
  Eye,
  XCircle,
  Clock,
  Users,
  Filter,
  Search,
  SortAsc,
  Grid,
  List,
  Globe,
  Lock,
  UserPlus,
  Check,
  AlertCircle,
  X,
  Settings,
  Loader2,
  UserX,
  Trash2,
  Image,
  Film,
  Music,
  FileSpreadsheet,
  Archive,
  Presentation,
  AlertTriangle
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import { notifyFileShare } from '../../lib/notificationService';

// Supabase istemcisini başlat
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Paylaşım türleri
const SHARE_TYPES = {
  PRIVATE: 'private',
  SPECIFIC: 'specific',
  PUBLIC: 'public'
};

// Paylaşım durumları
const SHARE_STATUS = {
  PUBLIC: 'public',
  SHARED_BY_ME: 'shared_by_me',
  SHARED_WITH_ME: 'shared_with_me'
};

const ShareOptions = ({ isOpen, onClose, file, onShareComplete }) => {
  const [shareType, setShareType] = useState(SHARE_TYPES.PRIVATE);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [existingShare, setExistingShare] = useState(null);

  // Kullanıcıları getir
  const fetchUsers = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Kullanıcı oturumu bulunamadı');
      }

      const { data: users, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email')
        .neq('id', user.id);

      if (error) throw error;
      setAvailableUsers(users || []);
    } catch (error) {
      console.error('Kullanıcılar alınırken hata:', error);
      toast.error('Kullanıcılar yüklenirken bir hata oluştu');
    }
  }, []);

  // Mevcut paylaşımı kontrol et
  const checkExistingShare = useCallback(async () => {
    if (!file?.id) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      // Mevcut paylaşım bilgilerini al
      const { data: shareData, error: shareError } = await supabase
        .from('shared_files')
        .select('id, share_type, created_at')
        .eq('file_id', file.id)
        .eq('shared_by', user.id)
        .maybeSingle();
      
      if (shareError) throw shareError;
      
      if (shareData) {
        setExistingShare(shareData);
        setShareType(shareData.share_type);
        
        // Eğer paylaşım tipi "specific" ise, paylaşılan kullanıcıları al
        if (shareData.share_type === SHARE_TYPES.SPECIFIC) {
          const { data: shareUsers, error: usersError } = await supabase
            .from('file_shares')
            .select('user_id')
            .eq('shared_file_id', shareData.id);
          
          if (usersError) throw usersError;
          
          if (shareUsers && shareUsers.length > 0) {
            // Paylaşılan kullanıcıların ID'lerini selectedUsers state'ine ekle
            setSelectedUsers(shareUsers.map(share => share.user_id));
          }
        }
      }
    } catch (error) {
      console.error('Paylaşım kontrolü sırasında hata:', error);
    }
  }, [file]);

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (isOpen) {
      checkExistingShare();
      fetchUsers();
    } else {
      // Modal kapandığında state'leri sıfırla
      setShareType(SHARE_TYPES.PRIVATE);
      setSelectedUsers([]);
      setSearchQuery('');
      setExistingShare(null);
    }
  }, [isOpen, checkExistingShare, fetchUsers]);

  // Paylaşım işlemini gerçekleştir
  const handleShare = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('Kullanıcı oturumu bulunamadı');

      // Belirli kişiler seçeneğinde kullanıcı seçimi kontrolü
      if (shareType === SHARE_TYPES.SPECIFIC && selectedUsers.length === 0) {
        throw new Error('Lütfen paylaşmak istediğiniz kullanıcıları seçin');
      }

      let shareId;
      
      // Mevcut paylaşımı güncelle veya yeni paylaşım oluştur
      if (existingShare) {
        // Her zaman updated_at alanını güncelle, paylaşım türü değişmiş olsa da olmasa da
        const { error: updateError } = await supabase
          .from('shared_files')
          .update({
            share_type: shareType,
            updated_at: new Date().toISOString(),
            is_active: true
          })
          .eq('id', existingShare.id);

        if (updateError) throw updateError;
        
        shareId = existingShare.id;
        
        // Eğer tür "private" olarak değiştiyse, mevcut tüm paylaşımları kaldır
        if (shareType === SHARE_TYPES.PRIVATE) {
          const { error: deleteError } = await supabase
            .from('file_shares')
            .delete()
            .eq('shared_file_id', shareId);
          
          if (deleteError) throw deleteError;
          
          toast.success('Paylaşım başarıyla kaldırıldı, artık bu dosya sadece size özeldir.');
          onClose();
          onShareComplete();
          return;
        }
      } else {
        // Yeni paylaşım oluştur
        const { data: newShare, error: createError } = await supabase
          .from('shared_files')
          .insert({
            file_id: file.id,
            shared_by: user.id,
            share_type: shareType,
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (createError) throw createError;
        shareId = newShare.id;
      }

      // Paylaşım türüne göre işlem yap
      if (shareType === SHARE_TYPES.PUBLIC) {
        // Eğer herkese açık ise, mevcut tüm paylaşımları kaldır ve bildirim gönder
        if (existingShare) {
          // Önce mevcut paylaşımları temizle
          await supabase
            .from('file_shares')
            .delete()
            .eq('shared_file_id', shareId);
        }
        
        // Herkese açık paylaşım için bildirim gönder
        try {
          const { data: userData } = await supabase
            .from('user_profiles')
            .select('first_name, last_name')
            .eq('id', user.id)
            .single();

          const sharerName = userData ? `${userData.first_name} ${userData.last_name}` : 'Bir kullanıcı';
          
          // Tüm kullanıcıları al
          const { data: allUsers } = await supabase
            .from('user_profiles')
            .select('id')
            .neq('id', user.id);
            
          if (allUsers && allUsers.length > 0) {
            // Bildirim gönder
            await sendNotifications(
              allUsers.map(u => u.id),
              user.id,
              shareId,
              true,
              file.file_name,
              sharerName
            );
          }
        } catch (notifyError) {
          console.error('Bildirim gönderirken hata:', notifyError);
          // Bildirim hatası paylaşımı engellemeyecek
        }
        
        toast.success(existingShare ? 'Dosya herkese açık olarak güncellendi' : 'Dosya herkese açık olarak paylaşıldı');
      } else if (shareType === SHARE_TYPES.SPECIFIC) {
        // Eğer mevcut paylaşım varsa ve önceden herkese açık ise, önce mevcut paylaşımları temizle
        if (existingShare && existingShare.share_type === 'public') {
          await supabase
            .from('file_shares')
            .delete()
            .eq('shared_file_id', shareId);
        }
        
        // Seçili kullanıcılarla paylaş
        // Önce mevcut paylaşımları kontrol et
        const { data: existingShares, error: checkError } = await supabase
          .from('file_shares')
          .select('user_id')
          .eq('shared_file_id', shareId);

        if (checkError) throw checkError;

        // Mevcut kullanıcı ID'lerini ve yeni kullanıcıları belirle
        const existingUserIds = existingShares?.map(share => share.user_id) || [];
        const newUsers = selectedUsers.filter(userId => !existingUserIds.includes(userId));
        
        // Eğer mevcut paylaşım varsa, artık seçili olmayan kullanıcıları kaldır
        if (existingShare) {
          const removedUsers = existingUserIds.filter(userId => !selectedUsers.includes(userId));
          
          if (removedUsers.length > 0) {
            const { error: removeError } = await supabase
              .from('file_shares')
              .delete()
              .eq('shared_file_id', shareId)
              .in('user_id', removedUsers);
              
            if (removeError) throw removeError;
          }
        }

        // Yeni kullanıcıları ekle
        if (newUsers.length > 0) {
          // Yeni paylaşımları oluştur
          const sharePromises = newUsers.map(userId =>
            supabase.from('file_shares').insert({
              shared_file_id: shareId,
              user_id: userId,
              permission_type: 'view',
              created_at: new Date().toISOString()
            })
          );

          const results = await Promise.all(sharePromises);
          const errors = results.filter(result => result.error);

          if (errors.length > 0) {
            console.error('Bazı paylaşımlar oluşturulamadı:', errors);
            throw new Error('Bazı kullanıcılarla paylaşım yapılamadı');
          }

          // Yeni kullanıcılara bildirim gönder
          try {
            const { data: userData } = await supabase
              .from('user_profiles')
              .select('first_name, last_name')
              .eq('id', user.id)
              .single();

            const sharerName = userData ? `${userData.first_name} ${userData.last_name}` : 'Bir kullanıcı';
            await sendNotifications(newUsers, user.id, shareId, false, file.file_name, sharerName);
          } catch (notifyError) {
            console.error('Bildirim gönderirken hata:', notifyError);
            // Bildirim hatası paylaşımı engellemeyecek
          }

          toast.success(existingShare ? 
            `Paylaşım güncellendi, ${newUsers.length} yeni kullanıcı eklendi` : 
            `Dosya ${newUsers.length} kullanıcı ile paylaşıldı`
          );
        } else if (existingShare) {
          // Kullanıcı eklemesi olmadı ama silme olabilir
          const removedUsers = existingUserIds.filter(userId => !selectedUsers.includes(userId));
          
          if (removedUsers.length > 0) {
            toast.success(`Paylaşım güncellendi, ${removedUsers.length} kullanıcı paylaşımdan çıkarıldı`);
          } else {
            toast.success('Paylaşım ayarları güncellendi, ancak yeni kullanıcı eklenmedi');
          }
        } else {
          toast.error('Paylaşılacak kullanıcı seçmediniz');
          setIsLoading(false);
          return;
        }
      }

      onClose();
      onShareComplete();
    } catch (error) {
      console.error('Paylaşım hatası:', error);
      toast.error(error.message || 'Dosya paylaşılırken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  // Bildirimleri gönder
  const sendNotifications = async (userIds, createdBy, shareId, isPublic, fileName, sharerName) => {
    try {
      // Her kullanıcı için bildirim oluştur
      const notificationPromises = userIds.map(userId =>
        notifyFileShare(
          userId,
          createdBy,
          sharerName,
          fileName,
          shareId
        )
      );

      await Promise.allSettled(notificationPromises);
    } catch (error) {
      console.warn('Bildirim gönderme işlemi sırasında hata:', error);
      // Bildirim hatası paylaşımı engellemeyecek
    }
  };

  // Kullanıcı seçim bileşeni
  const UserSelectionSection = () => {
    const [isSearching, setIsSearching] = useState(false);
    const searchDebounceRef = useRef(null);

    // Arama sonuçlarını memoize et
    const filteredUsers = useMemo(() => {
      return availableUsers.filter(user =>
        user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }, [availableUsers, searchQuery]);

    // Arama işlemini debounce et
    const handleSearch = (value) => {
      setIsSearching(true);
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      searchDebounceRef.current = setTimeout(() => {
        setSearchQuery(value);
        setIsSearching(false);
      }, 300);
    };

    // Profil fotoğrafı veya baş harfleri göster
    const UserAvatar = ({ user }) => {
      const initials = (user.first_name?.[0] || user.email[0]).toUpperCase();
      
      if (user.profile_photo_url) {
        return (
          <div className="relative w-10 h-10">
            <img
              src={user.profile_photo_url}
              alt={`${user.first_name || 'Kullanıcı'} profil fotoğrafı`}
              className="w-10 h-10 rounded-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div
              className="hidden w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-medium items-center justify-center"
            >
              {initials}
            </div>
          </div>
        );
      }

      return (
        <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
          <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
            {initials}
          </span>
        </div>
      );
    };

    return (
      <div className="mt-6 space-y-4">
        {/* Arama kutusu */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Ad, soyad veya e-posta ile ara..."
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500"
          />
          {isSearching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
          )}
        </div>

        {/* Kullanıcı listesi */}
        <div className="max-h-[50vh] overflow-y-auto space-y-2 custom-scrollbar">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-4">
              <UserX className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
                {searchQuery ? 'Arama kriterlerine uygun kullanıcı bulunamadı' : 'Paylaşılabilecek kullanıcı bulunamadı'}
              </p>
            </div>
          ) : (
            filteredUsers.map((user) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  selectedUsers.includes(user.id)
                    ? 'bg-primary-50 dark:bg-primary-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <UserAvatar user={user} />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {user.first_name} {user.last_name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user.email}
                    </p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    if (selectedUsers.includes(user.id)) {
                      setSelectedUsers(prev => prev.filter(id => id !== user.id));
                    } else {
                      setSelectedUsers(prev => [...prev, user.id]);
                    }
                  }}
                  className={`p-2 rounded-lg transition-colors ${
                    selectedUsers.includes(user.id)
                      ? 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
                  }`}
                >
                  <Check className="w-5 h-5" />
                </motion.button>
              </motion.div>
            ))
          )}
        </div>

        {/* Seçili kullanıcı sayısı */}
        {selectedUsers.length > 0 && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {selectedUsers.length} kullanıcı seçildi
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Share2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    {existingShare ? 'Paylaşım Ayarlarını Güncelle' : 'Dosya Paylaş'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {file.file_name}
                    {existingShare && (
                      <span className="ml-2 text-xs inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-500">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(existingShare.created_at).toLocaleDateString()} tarihinde paylaşıldı
                      </span>
                    )}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[80vh] overflow-y-auto custom-scrollbar">
              {existingShare && (
                <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0" />
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-amber-800 dark:text-amber-400">Bu dosya daha önce paylaşılmış</h4>
                      <p className="mt-1 text-sm text-amber-700 dark:text-amber-500">
                        Paylaşım türünü değiştirebilir veya yeni kullanıcılar ekleyebilirsiniz. Değişiklikleriniz mevcut paylaşımı güncelleyecektir.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Paylaşım türü seçenekleri */}
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setShareType(SHARE_TYPES.PRIVATE)}
                  className={`relative p-4 rounded-xl border transition-all ${
                    shareType === SHARE_TYPES.PRIVATE
                      ? 'bg-primary-50 border-primary-500 dark:bg-primary-900/20 dark:border-primary-500'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className={`p-3 rounded-lg ${
                      shareType === SHARE_TYPES.PRIVATE
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    }`}>
                      <Lock className="w-6 h-6" />
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-gray-900 dark:text-white">Özel</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Sadece ben</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setShareType(SHARE_TYPES.SPECIFIC)}
                  className={`relative p-4 rounded-xl border transition-all ${
                    shareType === SHARE_TYPES.SPECIFIC
                      ? 'bg-primary-50 border-primary-500 dark:bg-primary-900/20 dark:border-primary-500'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className={`p-3 rounded-lg ${
                      shareType === SHARE_TYPES.SPECIFIC
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    }`}>
                      <UserPlus className="w-6 h-6" />
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-gray-900 dark:text-white">Belirli Kişiler</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Seçili kullanıcılar</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setShareType(SHARE_TYPES.PUBLIC)}
                  className={`relative p-4 rounded-xl border transition-all ${
                    shareType === SHARE_TYPES.PUBLIC
                      ? 'bg-primary-50 border-primary-500 dark:bg-primary-900/20 dark:border-primary-500'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className={`p-3 rounded-lg ${
                      shareType === SHARE_TYPES.PUBLIC
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    }`}>
                      <Globe className="w-6 h-6" />
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-gray-900 dark:text-white">Herkese Açık</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Herkes görebilir</div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Kullanıcı seçimi */}
              {shareType === SHARE_TYPES.SPECIFIC && <UserSelectionSection />}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <Clock className="w-4 h-4 mr-1" />
                  Son güncelleme: {new Date().toLocaleDateString()}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleShare}
                    disabled={isLoading || (shareType === SHARE_TYPES.SPECIFIC && selectedUsers.length === 0)}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 ${
                      isLoading || (shareType === SHARE_TYPES.SPECIFIC && selectedUsers.length === 0)
                        ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800'
                    }`}
                  >
                    {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {isLoading ? 'İşleniyor...' : existingShare ? 'Paylaşımı Güncelle' : 'Paylaş'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// FileCard bileşenine dosya detayları popup'ı ekliyorum
const FileCard = ({ share, onShareClick, deletingFileId, setDeletingFileId }) => {
  const [showFileDetails, setShowFileDetails] = useState(false);
  const [sharedWithUsers, setSharedWithUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const fileName = share.file?.file_name || 'İsimsiz Dosya';
  const fileType = share.file?.file_type || '';
  const fileExt = fileName.split('.').pop();
  
  // Paylaşılan kullanıcıları getir
  const fetchSharedUsers = useCallback(async () => {
    if (!showFileDetails || share.share_type !== 'specific' || !share.id) return;
    
    try {
      setLoadingUsers(true);
      
      // Paylaşılan kullanıcıların ID'lerini al
      const { data: sharedUsers, error: shareError } = await supabase
        .from('file_shares')
        .select('user_id')
        .eq('shared_file_id', share.id);
      
      if (shareError) throw shareError;
      
      if (!sharedUsers || sharedUsers.length === 0) {
        setSharedWithUsers([]);
        return;
      }
      
      // Kullanıcı detaylarını al
      const { data: userDetails, error: userError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, profile_photo_url')
        .in('id', sharedUsers.map(u => u.user_id));
      
      if (userError) throw userError;
      
      setSharedWithUsers(userDetails || []);
    } catch (error) {
      console.error('Paylaşılan kullanıcılar alınırken hata:', error);
    } finally {
      setLoadingUsers(false);
    }
  }, [share.id, share.share_type, showFileDetails]);
  
  // Dosya detayları açıldığında paylaşılan kullanıcıları getir
  useEffect(() => {
    fetchSharedUsers();
  }, [fetchSharedUsers]);
  
  // Dosya boyutunu formatla
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Dosyanın önizleme URL'ini oluştur
  const getThumbnailUrl = () => {
    if (!share.file) return '';
    return `${import.meta.env.VITE_FILE_SUPABASE_URL}/storage/v1/object/public/files/${share.file.file_path}`;
  };

  // Paylaşım durumuna göre renk belirleme
  const getStatusColor = () => {
    switch (share.share_status) {
      case SHARE_STATUS.SHARED_BY_ME:
        return 'primary';
      case SHARE_STATUS.SHARED_WITH_ME:
        return 'blue';
      case SHARE_STATUS.PUBLIC:
        return 'green';
      default:
        return 'gray';
    }
  };

  // Dosya tipine göre uygun ikonu döndür
  const getFileIcon = () => {
    const iconClass = "w-6 h-6";
    
    if (fileType.startsWith('image/')) {
      return <Image className={iconClass} />;
    } else if (fileType.startsWith('video/')) {
      return <Film className={iconClass} />;
    } else if (fileType.startsWith('audio/')) {
      return <Music className={iconClass} />;
    } else if (fileType === 'application/pdf') {
      return <FileText className={iconClass} />;
    } else if (fileType.includes('word') || fileExt === 'doc' || fileExt === 'docx') {
      return <FileText className={iconClass} />;
    } else if (fileType.includes('excel') || fileExt === 'xls' || fileExt === 'xlsx') {
      return <FileSpreadsheet className={iconClass} />;
    } else if (fileType.includes('presentation') || fileExt === 'ppt' || fileExt === 'pptx') {
      return <Presentation className={iconClass} />;
    } else if (fileType === 'application/zip' || fileType === 'application/x-zip-compressed') {
      return <Archive className={iconClass} />;
    } else {
      return <File className={iconClass} />;
    }
  };

  // Dosya paylaşım bilgilerini doğru şekilde göster
  const renderShareInfo = () => {
    const color = getStatusColor();
    
    switch (share.share_status) {
      case SHARE_STATUS.SHARED_BY_ME:
        return (
          <>
            <span className="text-gray-600 dark:text-gray-400">{new Date(share.share_created_at || share.created_at).toLocaleDateString()}</span>
            <span className={`text-${color}-600 dark:text-${color}-400 ml-2`}>
              {share.share_type === 'private' ? 'Özel' : 
               share.share_type === 'specific' ? 'Belirli Kişiler' : 
               'Herkese Açık'}
            </span>
            <span className="text-gray-600 dark:text-gray-400 ml-auto">Sizin tarafınızdan paylaşıldı</span>
          </>
        );
      case SHARE_STATUS.SHARED_WITH_ME:
        return (
          <>
            <span className="text-gray-600 dark:text-gray-400">{new Date(share.share_created_at || share.created_at).toLocaleDateString()}</span>
            <span className={`text-${color}-600 dark:text-${color}-400 ml-2`}>
              {share.share_type === 'private' ? 'Özel' : 
               share.share_type === 'specific' ? 'Belirli Kişiler' : 
               'Herkese Açık'}
            </span>
            <span className="text-gray-600 dark:text-gray-400 ml-auto">
              Paylaşan: {share.shared_by_user?.first_name} {share.shared_by_user?.last_name}
            </span>
          </>
        );
      case SHARE_STATUS.PUBLIC:
        return (
          <>
            <span className="text-gray-600 dark:text-gray-400">{new Date(share.share_created_at || share.created_at).toLocaleDateString()}</span>
            <span className={`text-${color}-600 dark:text-${color}-400 ml-2`}>
              {share.share_type === 'private' ? 'Özel' : 
               share.share_type === 'specific' ? 'Belirli Kişiler' : 
               'Herkese Açık'}
            </span>
            <span className="text-gray-600 dark:text-gray-400 ml-auto">
              Paylaşan: {share.shared_by_user?.first_name} {share.shared_by_user?.last_name}
            </span>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="group cursor-pointer bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-md dark:hover:shadow-lg dark:hover:shadow-gray-800/30 hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-200 border border-gray-200 dark:border-gray-700 overflow-hidden relative flex flex-col">
        {/* Dosya Önizleme */}
        <div className="relative p-4 pt-6 h-32 flex items-center justify-center bg-gray-90 dark:bg-blue-100 overflow-hidden rounded-xl shadow-xl">
          <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium">
            {share.share_status === SHARE_STATUS.SHARED_BY_ME ? (
              <span className="text-primary-600 dark:text-primary-400">Sizin Paylaştığınız</span>
            ) : share.share_status === SHARE_STATUS.SHARED_WITH_ME ? (
              <span className="text-blue-600 dark:text-blue-400">Size Özel Paylaşılan</span>
            ) : (
              <span className="text-green-600 dark:text-green-400">Herkese Açık</span>
            )}
          </div>
          
          {/* Dosya Tipi İkonu */}
          <div className={`p-4 rounded-xl bg-${getStatusColor()}-100 dark:bg-${getStatusColor()}-900/30 text-${getStatusColor()}-600 dark:text-${getStatusColor()}-400`}>
            {getFileIcon()}
          </div>
        </div>
        
        {/* Dosya Bilgileri */}
        <div className="p-4 pt-2 flex-1 flex flex-col">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate ">
            {fileName}
          </h3>
          <div className="mt-2 flex items-center text-xs flex-wrap gap-y-1">
            {renderShareInfo()}
          </div>
        </div>
        
        {/* İşlem Butonları */}
        <div className="p-2 border-t border-gray-100 dark:border-gray-700 flex justify-end">
          <button 
            className="p-1.5 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Dosyayı görüntüle"
            onClick={() => setShowFileDetails(true)}
          >
            <Eye className="w-4 h-4" />
          </button>
          
          <button 
            className={`p-1.5 rounded-full ${
              share.share_status === SHARE_STATUS.SHARED_WITH_ME || (share.share_status === SHARE_STATUS.PUBLIC && !share.is_owner)
              ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            } transition-colors`}
            aria-label="Dosyayı paylaş"
            onClick={(e) => {
              e.stopPropagation();
              
              // Klasör kontrolü - klasörler paylaşılamaz
              if (share.file?.is_folder) {
                toast.error('Klasörler paylaşılamaz! Lütfen klasörü sıkıştırıp (rar, zip) arşiv olarak paylaşın.');
                return;
              }
              
              // Başkası tarafından paylaşılan dosyalar için
              if (share.share_status === SHARE_STATUS.SHARED_WITH_ME || (share.share_status === SHARE_STATUS.PUBLIC && !share.is_owner)) {
                toast.custom((t) => (
                  <div className={`max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 ${t.visible ? 'animate-enter' : 'animate-leave'}`}>
                    <div className="flex-1 w-0 p-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 pt-0.5">
                          <UserX className="h-5 w-5 text-rose-500" />
                        </div>
                        <div className="ml-3 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Paylaşım izniniz yok
                          </p>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {share.share_status === SHARE_STATUS.SHARED_WITH_ME
                              ? `Bu dosya sizinle ${share.shared_by_user?.first_name} ${share.shared_by_user?.last_name} tarafından paylaşılmış. Yalnızca dosyanın sahibi paylaşım ayarlarını değiştirebilir.`
                              : 'Bu herkese açık dosyayı yalnızca sahibi tekrar paylaşabilir veya paylaşım ayarlarını değiştirebilir.'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex border-l border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => toast.dismiss(t.id)}
                        className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 focus:outline-none"
                      >
                        Tamam
                      </button>
                    </div>
                  </div>
                ), { duration: 4000 });
                return;
              }
              
              // Eğer bu dosya kullanıcının kendi paylaştığı bir dosyaysa bilgilendirme göster
              if (share.share_status === SHARE_STATUS.SHARED_BY_ME) {
                toast.custom((t) => (
                  <div className={`max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 p-4 ${t.visible ? 'animate-enter' : 'animate-leave'}`}>
                    <div className="flex-1 w-0">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 pt-0.5">
                          <AlertCircle className="h-5 w-5 text-amber-500" />
                        </div>
                        <div className="ml-3 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Paylaşım ayarlarını düzenleyebilirsiniz
                          </p>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Bu dosya zaten paylaşılmış. Paylaşım türünü değiştirebilir veya yeni kullanıcılar ekleyebilirsiniz.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex border-l border-gray-200 dark:border-gray-700 ml-4 pl-4">
                      <button
                        onClick={() => {
                          toast.dismiss(t.id);
                          onShareClick(share.file);
                        }}
                        className="w-full rounded-md px-3 py-2 text-sm font-medium bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400"
                      >
                        Paylaşımı Düzenle
                      </button>
                    </div>
                  </div>
                ), { duration: 5000 });
              } else {
                onShareClick(share.file);
              }
            }}
          >
            <Share2 className="w-4 h-4" />
          </button>
          
          {share.share_status === SHARE_STATUS.SHARED_BY_ME && (
            <button 
              className="p-1.5 rounded-full text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              aria-label="Paylaşımı kaldır"
              onClick={(e) => {
                e.stopPropagation();
                setDeletingFileId(share.file.id);
                
                toast.custom((t) => (
                  <div className={`max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 ${t.visible ? 'animate-enter' : 'animate-leave'}`}>
                    <div className="flex-1 w-0 p-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 pt-0.5">
                          <AlertTriangle className="h-5 w-5 text-red-500" />
                        </div>
                        <div className="ml-3 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Paylaşımı kaldırmak istediğinizden emin misiniz?
                          </p>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Bu işlem geri alınamaz ve dosyanın paylaşım bilgileri veri tabanından silinecektir. Dosyanın kendisi sizin hesabınızda kalacaktır.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex border-l border-gray-200 dark:border-gray-700">
                      <button
                        onClick={async () => {
                          toast.dismiss(t.id);
                          
                          try {
                            // 1. Önce paylaşımları kaldır
                            const { error: shareDeleteError } = await supabase
                              .from('file_shares')
                              .delete()
                              .eq('shared_file_id', share.id);
                              
                            if (shareDeleteError) throw shareDeleteError;
                            
                            // 2. Sonra shared_files tablosundan kaydı sil
                            const { error: sharedFileError } = await supabase
                              .from('shared_files')
                              .delete()
                              .eq('id', share.id);
                              
                            if (sharedFileError) throw sharedFileError;
                            
                            toast.success("Paylaşım başarıyla kaldırıldı");
                            window.fetchSharedFiles && window.fetchSharedFiles(); // Listeyi yenile
                          } catch (error) {
                            console.error("Paylaşım kaldırılırken hata oluştu:", error);
                            toast.error("Paylaşım kaldırılırken bir hata oluştu: " + (error.message || 'Bilinmeyen hata'));
                          } finally {
                            setDeletingFileId(null);
                          }
                        }}
                        className="w-full border border-transparent rounded-none p-4 flex items-center justify-center text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none"
                      >
                        Kaldır
                      </button>
                      <button
                        onClick={() => {
                          toast.dismiss(t.id);
                          setDeletingFileId(null);
                        }}
                        className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none"
                      >
                        İptal
                      </button>
                    </div>
                  </div>
                ), { duration: 10000 });
              }}
            >
              {deletingFileId === share.file.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Dosya Detayları Popup'ı */}
      {showFileDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowFileDetails(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                Dosya Detayları
              </h3>
              <button
                onClick={() => setShowFileDetails(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Dosya Bilgileri */}
            <div className="p-6 max-h-[80vh] overflow-y-auto custom-scrollbar">
              <div className="flex gap-6">
                {/* Dosya Önizleme */}
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <div className={`p-6 rounded-xl bg-${getStatusColor()}-100 dark:bg-${getStatusColor()}-900/30 text-${getStatusColor()}-600 dark:text-${getStatusColor()}-400`}>
                      {getFileIcon()}
                    </div>
                  </div>
                </div>

                {/* Dosya Bilgileri */}
                <div className="flex-1">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    {fileName}
                  </h4>
                  
                  <div className="mt-4 space-y-3">
                    <div className="flex items-start">
                      <div className="w-32 flex-shrink-0 text-gray-500 dark:text-gray-400">Dosya Türü:</div>
                      <div className="text-gray-900 dark:text-white">{fileType || 'Bilinmiyor'}</div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-32 flex-shrink-0 text-gray-500 dark:text-gray-400">Dosya Boyutu:</div>
                      <div className="text-gray-900 dark:text-white">{formatFileSize(share.file?.file_size || 0)}</div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-32 flex-shrink-0 text-gray-500 dark:text-gray-400">Oluşturulma:</div>
                      <div className="text-gray-900 dark:text-white">
                        {new Date(share.file?.created_at).toLocaleString()}
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-32 flex-shrink-0 text-gray-500 dark:text-gray-400">Paylaşım Türü:</div>
                      <div className="text-gray-900 dark:text-white">
                        {share.share_status === SHARE_STATUS.SHARED_BY_ME ? 'Sizin Paylaştığınız' :
                         share.share_status === SHARE_STATUS.SHARED_WITH_ME ? 'Size Özel Paylaşılan' :
                         'Herkese Açık'}
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-32 flex-shrink-0 text-gray-500 dark:text-gray-400">Paylaşan:</div>
                      <div className="text-gray-900 dark:text-white">
                        {share.share_status === SHARE_STATUS.SHARED_BY_ME ? 
                          'Siz' : 
                          `${share.shared_by_user?.first_name || ''} ${share.shared_by_user?.last_name || ''}`}
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-32 flex-shrink-0 text-gray-500 dark:text-gray-400">Paylaşım Tarihi:</div>
                      <div className="text-gray-900 dark:text-white">
                        {new Date(share.share_created_at || share.created_at).toLocaleString()}
                      </div>
                    </div>
                    
                    {/* Dosya Belirli Kişilerle Paylaşılmışsa */}
                    {share.share_type === 'specific' && (
                      <div className="flex items-start pt-3 border-t border-gray-200 dark:border-gray-700 mt-3">
                        <div className="w-32 flex-shrink-0 text-gray-500 dark:text-gray-400">
                          Paylaşılan Kişiler:
                        </div>
                        <div className="flex-1">
                          {loadingUsers ? (
                            <div className="flex items-center space-x-2">
                              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                Kullanıcılar yükleniyor...
                              </span>
                            </div>
                          ) : sharedWithUsers.length === 0 ? (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              Belirli kişilerle paylaşılmamış ya da kullanıcı bulunamadı.
                            </span>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {sharedWithUsers.map(user => (
                                <div 
                                  key={user.id}
                                  className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-full"
                                  title={`${user.first_name || ''} ${user.last_name || ''} (${user.email})`}
                                >
                                  {/* Kullanıcı avatarı */}
                                  <div className="w-6 h-6 rounded-full overflow-hidden bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
                                    {user.profile_photo_url ? (
                                      <img 
                                        src={user.profile_photo_url} 
                                        alt={`${user.first_name || 'Kullanıcı'} avatarı`}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextElementSibling.style.display = 'flex';
                                        }}
                                      />
                                    ) : (
                                      <div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                                        <span className="text-xs font-medium text-primary-700 dark:text-primary-300">
                                          {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Kullanıcı adı */}
                                  <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {user.first_name && user.last_name 
                                      ? `${user.first_name} ${user.last_name}` 
                                      : user.email}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-end">
            <button
  onClick={() => window.open(getThumbnailUrl(), '_blank')}
  className="relative mr-2 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-blue-700 dark:text-blue-100 backdrop-blur-md bg-blue-100/30 dark:bg-blue-500/10 border border-blue-300 dark:border-blue-300/30 rounded-xl shadow-sm hover:bg-blue-200 dark:hover:bg-blue-500/20 hover:border-blue-400 dark:hover:border-blue-400/50 transition-all duration-300 ease-in-out focus:outline-none active:scale-95"
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5 text-blue-700 dark:text-blue-100"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
    />
  </svg>
  Dosyayı İndir
</button>



              <button
                onClick={() => setShowFileDetails(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const SharedFiles = () => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [filterType, setFilterType] = useState('all');
  const [sharedFiles, setSharedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = React.useRef(0);
  const [deletingFileId, setDeletingFileId] = useState(null);

  // Kullanıcı bilgisini al
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
    };
    getCurrentUser();
  }, []);

  // Paylaşılan dosyaları getir
  const fetchSharedFiles = useCallback(async () => {
    try {
      setLoading(true);
      
      // Kullanıcı kimliğini al
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setLoading(false);
        return;
      }
      
      // 1. Benim paylaştığım ve herkese açık dosyaları al
      const { data: myAndPublicShares, error: mySharesError } = await supabase
        .from('shared_files')
        .select('*')
        .or(`shared_by.eq.${user.id},share_type.eq.public`);
      
      if (mySharesError) throw mySharesError;
      
      // 2. Benimle paylaşılan dosyaların ID'lerini al
      const { data: sharedWithMe, error: sharedWithMeError } = await supabase
        .from('file_shares')
        .select('id, shared_file_id, permission_type, created_at')
        .eq('user_id', user.id);
      
      if (sharedWithMeError) throw sharedWithMeError;
      
      // Benimle paylaşılan dosyaların benzersiz shared_file_id'lerini al
      const sharedWithMeFileIds = sharedWithMe?.map(share => share.shared_file_id) || [];
      
      // 3. Benimle paylaşılan dosyaların detaylarını al
      const { data: sharedWithMeDetails, error: detailsError } = await supabase
        .from('shared_files')
        .select('*')
        .in('id', sharedWithMeFileIds);
      
      if (detailsError) throw detailsError;
      
      // 4. Tüm benzersiz storage dosya ID'lerini topla
      const allStorageFileIds = new Set();
      
      myAndPublicShares?.forEach(share => {
        if (share.file_id) allStorageFileIds.add(share.file_id);
      });
      
      sharedWithMeDetails?.forEach(share => {
        if (share.file_id) allStorageFileIds.add(share.file_id);
      });
      
      if (allStorageFileIds.size === 0) {
        setSharedFiles([]);
        setLoading(false);
        return;
      }
      
      // 5. Dosya bilgilerini al
      const { data: filesData, error: filesError } = await supabase
        .from('storage_files')
        .select('*')
        .in('id', Array.from(allStorageFileIds));
      
      if (filesError) throw filesError;
      
      // 6. Kullanıcı bilgilerini al
      const userIds = new Set();
      
      myAndPublicShares?.forEach(share => {
        if (share.shared_by) userIds.add(share.shared_by);
      });
      
      sharedWithMeDetails?.forEach(share => {
        if (share.shared_by) userIds.add(share.shared_by);
      });
      
      const { data: usersData, error: usersError } = await supabase
        .from('user_profiles')
        .select('*')
        .in('id', Array.from(userIds));
      
      if (usersError) throw usersError;
      
      // 7. Tüm verileri birleştir
      const allShares = [];
      
      // Benim paylaştığım dosyalar
      myAndPublicShares?.forEach(share => {
        if (share.shared_by === user.id) {
          const file = filesData?.find(f => f.id === share.file_id);
          const sharedByUser = usersData?.find(u => u.id === share.shared_by);
          
          if (file) {
            allShares.push({
              id: share.id,
              file_id: share.file_id,
              shared_by: share.shared_by,
              share_type: share.share_type,
              created_at: share.created_at,
              file: file,
              shared_by_user: sharedByUser,
              is_owner: true,
              share_status: SHARE_STATUS.SHARED_BY_ME,
              share_created_at: share.created_at
            });
          }
        }
      });
      
      // Herkese açık dosyalar (benim paylaştıklarım hariç)
      myAndPublicShares?.forEach(share => {
        if (share.share_type === 'public' && share.shared_by !== user.id) {
          const file = filesData?.find(f => f.id === share.file_id);
          const sharedByUser = usersData?.find(u => u.id === share.shared_by);
          
          if (file) {
            allShares.push({
              id: share.id,
              file_id: share.file_id,
              shared_by: share.shared_by,
              share_type: share.share_type,
              created_at: share.created_at,
              file: file,
              shared_by_user: sharedByUser,
              is_owner: false,
              share_status: SHARE_STATUS.PUBLIC,
              share_created_at: share.created_at
            });
          }
        }
      });
      
      // Benimle paylaşılan dosyalar
      sharedWithMeDetails?.forEach(shareDetail => {
        const fileShare = sharedWithMe?.find(s => s.shared_file_id === shareDetail.id);
        
        if (fileShare && shareDetail.shared_by !== user.id) {
          const file = filesData?.find(f => f.id === shareDetail.file_id);
          const sharedByUser = usersData?.find(u => u.id === shareDetail.shared_by);
          
          if (file) {
            allShares.push({
              id: shareDetail.id,
              file_id: shareDetail.file_id,
              shared_by: shareDetail.shared_by,
              share_type: shareDetail.share_type,
              created_at: shareDetail.created_at,
              file: file,
              shared_by_user: sharedByUser,
              is_owner: false,
              share_status: SHARE_STATUS.SHARED_WITH_ME,
              permission_type: fileShare.permission_type,
              share_created_at: fileShare.created_at
            });
          }
        }
      });
      
      // 8. Yinelemeleri kaldır
      const uniqueShares = [];
      const idSet = new Set();
      
      allShares.forEach(share => {
        if (!idSet.has(share.id)) {
          idSet.add(share.id);
          uniqueShares.push(share);
        }
      });
      
      // 9. Tarihe göre sırala
      uniqueShares.sort((a, b) => 
        new Date(b.share_created_at || b.created_at).getTime() - 
        new Date(a.share_created_at || a.created_at).getTime()
      );
      
      setSharedFiles(uniqueShares);
    } catch (error) {
      console.error('Paylaşılan dosyalar alınırken hata:', error);
      toast.error('Dosyalar yüklenirken bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Realtime aboneliklerini ayarla
  useEffect(() => {
    // İlk yüklemede verileri getir
    fetchSharedFiles();

    // Realtime abonelikleri oluştur
    const sharedFilesSubscription = supabase
      .channel('shared_files_changes')
      .on('postgres_changes', {
        event: '*', // INSERT, UPDATE ve DELETE olaylarını dinle
        schema: 'public',
        table: 'shared_files'
      }, () => {
        // Herhangi bir değişiklikte tüm listeyi yenile
        fetchSharedFiles();
      })
      .subscribe();
    
    const fileSharesSubscription = supabase
      .channel('file_shares_changes')
      .on('postgres_changes', {
        event: '*', // INSERT ve DELETE olaylarını dinle
        schema: 'public',
        table: 'file_shares'
      }, () => {
        // Herhangi bir değişiklikte tüm listeyi yenile
        fetchSharedFiles();
      })
      .subscribe();

    // Temizlik fonksiyonu: Abonelikleri iptal et
    return () => {
      supabase.removeChannel(sharedFilesSubscription);
      supabase.removeChannel(fileSharesSubscription);
    };
  }, [fetchSharedFiles]);

  // Global bir erişim sağlayarak dosya listesini yenileme fonksiyonunu erişilebilir yap
  useEffect(() => {
    window.fetchSharedFiles = fetchSharedFiles;
    
    return () => {
      delete window.fetchSharedFiles;
    };
  }, [fetchSharedFiles]);

  // Drag & Drop işleyicileri
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    try {
      const fileData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (fileData) {
        // Eğer klasör ise paylaşıma izin verme
        if (fileData.is_folder) {
          toast.error('Klasörler paylaşılamaz! Lütfen klasörü sıkıştırıp (rar, zip) arşiv olarak paylaşın.');
          return;
        }
        
        // Dosya verisi doğru şekilde alındı, modalı aç
        setSelectedFile(fileData);
        setShowShareModal(true);
      } else {
        // Dosya verisi alınamadıysa kullanıcıya bildir
        toast.error('Geçersiz dosya formatı');
      }
    } catch (error) {
      console.error('Dosya verisi alınamadı:', error);
      toast.error('Dosya paylaşılırken bir hata oluştu');
    }
  };

  // Filtrelenmiş ve sıralanmış dosyaları hesapla
  const filteredFiles = useMemo(() => {
    return sharedFiles
      .filter(share => {
        // Arama filtresi
        if (searchQuery && !share.file.file_name.toLowerCase().includes(searchQuery.toLowerCase())) {
          return false;
        }

        // Paylaşım türü filtresi
        if (filterType === 'shared-by-me') {
          return share.share_status === 'shared_by_me';
        } else if (filterType === 'shared-with-me') {
          return share.share_status === 'shared_with_me';
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'date') {
          return new Date(b.share_created_at || b.created_at).getTime() - 
                 new Date(a.share_created_at || a.created_at).getTime();
        } else if (sortBy === 'name') {
          return a.file.file_name.localeCompare(b.file.file_name);
        }
        return 0;
      });
  }, [sharedFiles, searchQuery, filterType, sortBy]);

  return (
    <div>
      <motion.div 
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6 relative transition-colors duration-200 ${
          isDragging 
            ? 'border-2 border-dashed border-primary-500 dark:border-primary-400 bg-primary-50/50 dark:bg-primary-900/20' 
            : 'border border-solid border-gray-200 dark:border-gray-700'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        animate={{
          scale: isDragging ? 1.002 : 1,
        }}
        transition={{
          type: "tween",
          duration: 0.2,
          ease: "easeInOut"
        }}
      >
        <AnimatePresence mode="wait">
          {isDragging && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeInOut" }}
              className="absolute inset-0 bg-primary-50/90 dark:bg-primary-900/40 backdrop-blur-[2px] rounded-lg flex items-center justify-center z-10"
            >
              <motion.div 
                className="text-center"
                initial={{ y: 5, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -5, opacity: 0 }}
                transition={{ duration: 0.2, delay: 0.05 }}
              >
                <motion.div 
                  className="p-3 rounded-full bg-primary-100 dark:bg-primary-900/50 inline-block mb-3"
                  animate={{ 
                    scale: [1, 1.05, 1],
                  }}
                  transition={{ 
                    duration: 1,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <Share2 className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </motion.div>
                <motion.h3 
                  className="text-lg font-medium text-primary-700 dark:text-primary-300"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  Dosyayı buraya bırakın
                </motion.h3>
                <motion.p 
                  className="text-sm text-primary-600/90 dark:text-primary-400/90 mt-1"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, delay: 0.1 }}
                >
                  Paylaşmak için dosyayı sürükleyip bırakın
                </motion.p>
                <motion.p 
                  className="text-xs text-amber-600/90 dark:text-amber-400/90 mt-1 max-w-xs"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, delay: 0.2 }}
                >
                  Not: Klasörler paylaşılamaz. Lütfen klasörü sıkıştırıp arşiv olarak paylaşın.
                </motion.p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Üst Başlık ve Kontroller */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Share2 className="w-6 h-6 text-purple-500" />
              Paylaşılan Dosyalar
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Paylaştığınız ve sizinle paylaşılan dosyaları yönetin
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Görünüm Modu */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md ${
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-gray-600 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-gray-600 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Sıralama */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-100 dark:bg-gray-700 px-3 py-1.5"
            >
              <option value="date">Son Paylaşılanlar</option>
              <option value="name">İsme Göre</option>
            </select>
          </div>
        </div>

        {/* Arama ve Filtreler */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Dosya ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'all'
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              Tümü
            </button>
            <button
              onClick={() => setFilterType('shared-by-me')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'shared-by-me'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              Paylaştıklarım
            </button>
            <button
              onClick={() => setFilterType('shared-with-me')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'shared-with-me'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              Benimle Paylaşılanlar
            </button>
          </div>
        </div>

        {/* Dosya Listesi */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">Dosyalar yükleniyor...</p>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="text-center py-12">
            <Share2 className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-pulse" />
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Henüz Paylaşılan Dosya Yok</h4>
            <p className="text-gray-500 dark:text-gray-400 animate-pulse">
              Dosyaları buraya sürükleyerek veya "Paylaş" butonunu kullanarak paylaşabilirsiniz.
            </p>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-3'}>
            {filteredFiles.map((share) => (
              <FileCard
                key={share.id}
                share={share}
                onShareClick={(file) => {
                  setSelectedFile(file);
                  setShowShareModal(true);
                }}
                deletingFileId={deletingFileId}
                setDeletingFileId={setDeletingFileId}
              />
            ))}
          </div>
        )}
      </motion.div>

      {/* Paylaşım Modalı */}
      {showShareModal && selectedFile && (
        <ShareOptions
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setSelectedFile(null);
          }}
          file={selectedFile}
          onShareComplete={fetchSharedFiles}
        />
      )}
    </div>
  );
};

export default SharedFiles; 